import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit, Inventory } from "@mui/icons-material";
import {
  useGetSheqCatalogueQuery,
  useCreateSheqCatalogueItemMutation,
  useUpdateSheqCatalogueItemMutation,
  useDeleteSheqCatalogueItemMutation,
  SheqCatalogueItem,
} from "../../../redux/sheqApi";

const NAVY = "#123456";

/** Safely parse availableSizes – handles JSON arrays, comma-separated strings, or single values */
function parseSizes(raw: string): string[] {
  if (!raw) return [];
  const trimmed = raw.trim();
  if (trimmed.startsWith("[")) {
    try {
      return JSON.parse(trimmed);
    } catch {
      /* fall through */
    }
  }
  return trimmed
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

const RISK_CATEGORIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"] as const;
const RISK_COLOR: Record<string, string> = {
  LOW: "#2e7d32",
  MEDIUM: "#f57c00",
  HIGH: "#c62828",
  CRITICAL: "#4a148c",
};

// ─── Catalogue dialog ────────────────────────────────────────────────────────
interface CatalogueDialogProps {
  open: boolean;
  item: SheqCatalogueItem | null;
  onClose: () => void;
}

const emptyItem = (): Partial<SheqCatalogueItem> => ({
  code: "",
  name: "",
  replacementCycleDays: 365,
  requiresSize: false,
  riskCategory: "MEDIUM",
  isActive: true,
});

const CatalogueDialog: React.FC<CatalogueDialogProps> = ({
  open,
  item,
  onClose,
}) => {
  const [create, { isLoading: creating }] =
    useCreateSheqCatalogueItemMutation();
  const [update, { isLoading: updating }] =
    useUpdateSheqCatalogueItemMutation();

  const [form, setForm] = useState<Partial<SheqCatalogueItem>>(
    item ?? emptyItem(),
  );

  React.useEffect(() => {
    setForm(item ?? emptyItem());
  }, [item, open]);

  const set = (key: keyof SheqCatalogueItem, val: unknown) =>
    setForm((f) => ({ ...f, [key]: val }));

  const editing = !!item;
  const saving = creating || updating;

  const handleSave = async () => {
    if (!form.code || !form.name || !form.replacementCycleDays) return;
    const payload: Omit<SheqCatalogueItem, "id" | "createdAt"> = {
      code: form.code!.toUpperCase().trim(),
      name: form.name!,
      description: form.description,
      replacementCycleDays: Number(form.replacementCycleDays),
      requiresSize: form.requiresSize ?? false,
      availableSizes: form.availableSizes,
      riskCategory: form.riskCategory ?? "MEDIUM",
      legislativeRef: form.legislativeRef,
      isActive: form.isActive ?? true,
      isStandardIssue: form.isStandardIssue ?? false,
    };
    try {
      if (editing && item?.id) {
        await update({ id: item.id, body: payload }).unwrap();
      } else {
        await create(payload).unwrap();
      }
      onClose();
    } catch {
      // handle
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? "Edit PPE Item" : "Add PPE Item"}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <Box display="flex" gap={2}>
            <TextField
              label="Code"
              size="small"
              required
              value={form.code ?? ""}
              onChange={(e) => set("code", e.target.value)}
              sx={{ width: 100 }}
              inputProps={{ maxLength: 6 }}
              helperText="e.g. HH, WS"
            />
            <TextField
              label="Name"
              size="small"
              required
              value={form.name ?? ""}
              onChange={(e) => set("name", e.target.value)}
              sx={{ flex: 1 }}
            />
          </Box>

          <TextField
            label="Description"
            size="small"
            value={form.description ?? ""}
            onChange={(e) => set("description", e.target.value)}
          />

          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              label="Replacement cycle (days)"
              type="number"
              size="small"
              required
              value={form.replacementCycleDays ?? 365}
              onChange={(e) =>
                set("replacementCycleDays", Number(e.target.value))
              }
              inputProps={{ min: 1 }}
              sx={{ flex: "1 1 160px" }}
            />
            <FormControl size="small" sx={{ flex: "1 1 140px" }}>
              <InputLabel>Risk Category</InputLabel>
              <Select
                label="Risk Category"
                value={form.riskCategory ?? "MEDIUM"}
                onChange={(e) => set("riskCategory", e.target.value)}
              >
                {RISK_CATEGORIES.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box display="flex" gap={3} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={form.requiresSize ?? false}
                  onChange={(e) => set("requiresSize", e.target.checked)}
                  size="small"
                />
              }
              label="Requires size"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isStandardIssue ?? false}
                  onChange={(e) => set("isStandardIssue", e.target.checked)}
                  size="small"
                />
              }
              label="Standard issue (all workers)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive ?? true}
                  onChange={(e) => set("isActive", e.target.checked)}
                  size="small"
                />
              }
              label="Active"
            />
          </Box>

          {form.requiresSize && (
            <TextField
              label='Available sizes (JSON array, e.g. ["S","M","L","XL"])'
              size="small"
              value={form.availableSizes ?? ""}
              onChange={(e) => set("availableSizes", e.target.value)}
              helperText='Must be valid JSON array: ["S","M","L"]'
            />
          )}

          <TextField
            label="Legislative Reference"
            size="small"
            value={form.legislativeRef ?? ""}
            onChange={(e) => set("legislativeRef", e.target.value)}
            placeholder="e.g. OHS Act Section 8"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !form.code || !form.name}
          sx={{ bgcolor: NAVY }}
        >
          {saving ? <CircularProgress size={16} /> : editing ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────
export const SheqCatalogue: React.FC = () => {
  const { data: items = [], isLoading } = useGetSheqCatalogueQuery();
  const [deleteItem] = useDeleteSheqCatalogueItemMutation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<SheqCatalogueItem | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const filtered = showInactive ? items : items.filter((i) => i.isActive);

  const handleDelete = async (id: string) => {
    if (!window.confirm("Remove this PPE catalogue item?")) return;
    await deleteItem(id);
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h4" fontWeight={700}>
          PPE Catalogue
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                size="small"
              />
            }
            label="Show inactive"
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditItem(null);
              setDialogOpen(true);
            }}
            sx={{ bgcolor: NAVY }}
          >
            Add Item
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Box textAlign="center" py={8}>
          <Inventory sx={{ fontSize: 56, color: "#ccc", mb: 2 }} />
          <Typography color="text.secondary">
            No PPE catalogue items.
          </Typography>
        </Box>
      ) : (
        <Card>
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f5f5f5" }}>
                  {[
                    "Code",
                    "Name",
                    "Standard",
                    "Cycle",
                    "Risk",
                    "Sizing",
                    "Leg. Ref",
                    "Active",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "10px 12px",
                        textAlign: "left",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#555",
                        whiteSpace: "nowrap",
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr
                    key={item.id}
                    style={{
                      borderBottom: "1px solid #f0f0f0",
                      opacity: item.isActive ? 1 : 0.5,
                    }}
                  >
                    <td style={{ padding: "9px 12px" }}>
                      <Typography
                        component="span"
                        sx={{
                          bgcolor: NAVY,
                          color: "#fff",
                          px: 0.8,
                          py: 0.3,
                          borderRadius: 0.5,
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        {item.code}
                      </Typography>
                    </td>
                    <td style={{ padding: "9px 12px", fontSize: 13 }}>
                      {item.name}
                      {item.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {item.description}
                        </Typography>
                      )}
                    </td>
                    <td style={{ padding: "9px 12px", textAlign: "center" }}>
                      {item.isStandardIssue ? (
                        <Chip
                          label="Standard"
                          size="small"
                          sx={{
                            bgcolor: "#1565c0",
                            color: "#fff",
                            fontSize: 10,
                            height: 20,
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </td>
                    <td style={{ padding: "9px 12px", fontSize: 13 }}>
                      {item.replacementCycleDays < 30
                        ? `${item.replacementCycleDays}d`
                        : item.replacementCycleDays < 365
                        ? `${Math.round(item.replacementCycleDays / 30)}mo`
                        : `${Math.round(item.replacementCycleDays / 365)}yr`}
                    </td>
                    <td style={{ padding: "9px 12px" }}>
                      <Chip
                        label={item.riskCategory}
                        size="small"
                        sx={{
                          bgcolor: RISK_COLOR[item.riskCategory] ?? "#555",
                          color: "#fff",
                          fontSize: 11,
                        }}
                      />
                    </td>
                    <td style={{ padding: "9px 12px", fontSize: 13 }}>
                      {item.requiresSize ? "Yes" : "No"}
                      {item.requiresSize && item.availableSizes && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {parseSizes(item.availableSizes).join(", ") ||
                            item.availableSizes}
                        </Typography>
                      )}
                    </td>
                    <td
                      style={{
                        padding: "9px 12px",
                        fontSize: 12,
                        color: "#777",
                      }}
                    >
                      {item.legislativeRef ?? "—"}
                    </td>
                    <td style={{ padding: "9px 12px", fontSize: 13 }}>
                      {item.isActive ? "✓" : "✗"}
                    </td>
                    <td style={{ padding: "9px 12px" }}>
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditItem(item);
                              setDialogOpen(true);
                            }}
                          >
                            <Edit sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(item.id!)}
                          >
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Card>
      )}

      <CatalogueDialog
        open={dialogOpen}
        item={editItem}
        onClose={() => {
          setDialogOpen(false);
          setEditItem(null);
        }}
      />
    </Box>
  );
};
